import { MAX_U16, effectByNumber, type Effect, type EffectsRecord, type Rules } from "odyssey-shared";
import { durationText, signedPercent } from "../bar/units.ts";
import { PENALTY_MODIFIERS, penaltyCost, penaltyModifier, penaltyModifierWords } from "./penalty-words.ts";

/**
 * The death screen's cost lines (P2.5-T7, P2.2-T23; ADR-079's amendment of 2026-10-04, item 7): what the death the
 * player lies in brought, drafted from each penalty's record as the tooltips are and never written per record. The page
 * decides nothing here: the server applies the penalty at the kill and the owner's `effects` frame carries its number,
 * its stacks and its time, which this prints. A modifier is counted once per stack, the rule the effects row's tooltip
 * and the sums already read the record by. Its words are the language files' (`death.*`). Pure.
 */

/**
 * One penalty's cost line: its name, each modifier it holds at its stacks, and its time.
 *
 * @param effect The penalty's record.
 * @param stacks The stacks the frame carries; fewer than 1 reads as 1.
 * @param ticksLeft The time the frame carries, in ticks.
 * @returns The line.
 * @example
 * costLine(rules.effects.get("resurrection_sickness"), 3, 2400);
 * // "Resurrection Sickness: -15% damage dealt, -6% maximum health, 2 min"
 */
export function costLine(effect: Effect, stacks: number, ticksLeft: number): string {
  const held = Math.max(1, stacks);
  const parts: string[] = [];
  for (const field of PENALTY_MODIFIERS) {
    const percent = effect.modifiers[field] * held;
    if (percent !== 0) {
      parts.push(penaltyModifier(signedPercent(percent), penaltyModifierWords(field)));
    }
  }
  parts.push(durationText(ticksLeft));
  return penaltyCost(effect.name, parts);
}

/**
 * The cost lines of the death a frame was sent in, in the frame's order. A death's penalty is applied stopped at its
 * record's full time (`Effects.die`), and a penalty held from an earlier death has run part of its time, so the
 * penalties standing at their full time are the ones this death brought or restarted; a heavier penalty a lighter death
 * restarted reads at the stacks held, which is what the character stands up with. A frame of a living character lists
 * a penalty at its full time only in the tick it stands up, so the caller shows these only while the character is dead.
 * The frame's ticks stop at 65,535, so a penalty longer than that would read as this death's for as long as its time
 * left stays above it; no shipped penalty is longer than 12,000.
 *
 * @param rules The page's rules, which name each effect by its number.
 * @param frame The decoded owner's `effects` frame, its first `count` entries valid.
 * @returns The lines, none when the frame holds no penalty at its full time; an effect the rules lack says nothing.
 * @example
 * deathCosts(rules, { count: 1, effects: [{ effect: 2, ticksLeft: 2400, stacks: 1 }] });
 * // ["Resurrection Sickness: -5% damage dealt, -2% maximum health, 2 min"]
 */
export function deathCosts(rules: Rules, frame: Readonly<EffectsRecord>): readonly string[] {
  const lines: string[] = [];
  for (let index = 0; index < frame.count; index += 1) {
    const entry = frame.effects[index];
    const effect = entry === undefined ? undefined : effectByNumber(rules, entry.effect);
    if (entry !== undefined && effect !== undefined && effect.onDeath !== null && entry.ticksLeft >= Math.min(MAX_U16, effect.durationTicks)) {
      lines.push(costLine(effect, entry.stacks, entry.ticksLeft));
    }
  }
  return lines;
}
